import React, { useState, useEffect, useMemo } from 'react';
import CountryList from 'react-select-country-list';
import { useForm } from 'react-hook-form';
import DeviceUnitsField from '../common/DeviceUnitsField';
import './FacilityModal.css';
import {
  BaseModalV2,
  Input,
  Label,
  ErrorMessage,
  PrimaryButton,
  SecondaryButton,
  SelectField,
  ToggleButton,
} from '../common';
import { useFeedbackContext } from '../../context';
import getNonEmptyArray from '../../utils/arrays';
import useDeviceAssociation from '../../hooks/useDeviceAssociation';
import { ACTION_TYPES, EMPTY_FIELD_ERROR, TOGGLE_STATUSES } from '../../constants';
import { serviceLogger } from '../../utils/serviceLogger';

const FacilityModal = ({ action, facility = {}, address = {}, onSave, onClose }) => {
  const { showToast } = useFeedbackContext();

  const {
    deviceIdentifier,
    showDeviceInput,
    setShowDeviceInput,
    showDeviceToggle,
    handleDeviceAssociation,
  } = useDeviceAssociation(facility, action);

  /* ───────── valores iniciales de Device Units ───────── */
  const initialDeviceIds = useMemo(() => {
    if (Array.isArray(deviceIdentifier) && deviceIdentifier.length) {
      return deviceIdentifier.map((d) => (typeof d === 'string' ? d : d.esp32_mac_address));
    }
    return ['']; // al menos un input vacío
  }, [deviceIdentifier]);

  const deviceInputStatus = showDeviceInput ? TOGGLE_STATUSES.ENABLED : TOGGLE_STATUSES.DISABLED;

  /* ───────── valores iniciales de Facility / Address ───────── */
  const initialValues = useMemo(
    () => ({
      name: facility.name || '',
      type: facility.type || '',
      street: address.street || '',
      city: address.city || '',
      state: address.state || '',
      zip_code: address.zip_code || '',
      country: address.country === 'USA' ? 'US' : address.country || '',
    }),
    [facility, address],
  );

  const blankValues = useMemo(
    () => ({
      name: '',
      type: '',
      street: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
    }),
    [],
  );

  /* ───────── react-hook-form ───────── */
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      ...initialValues,
      deviceIdentifiers: initialDeviceIds, // dentro de defaultValues
    },
    mode: 'onChange',
  });

  /* ───────── opciones de país ───────── */
  const [countryOptions, setCountryOptions] = useState([]);
  useEffect(() => {
    const countries = CountryList().getData();
    setCountryOptions(countries.map((c) => ({ id: c.value, name: c.label })));
  }, []);

  /* ───────── reset cuando cambia acción ───────── */
  useEffect(() => {
    reset(
      action === ACTION_TYPES.EDIT
        ? { ...initialValues, deviceIdentifiers: initialDeviceIds }
        : { ...blankValues, deviceIdentifiers: [''] },
    );
  }, [action, initialValues, blankValues, initialDeviceIds, reset]);

  /* ───────── submit ───────── */
  const onSubmit = async (data) => {
    const facilityDetails = { name: data.name, type: data.type };
    const hasAddress = data.street || data.city || data.state || data.zip_code || data.country;
    const addressDetails = hasAddress
      ? {
          street: data.street,
          city: data.city,
          state: data.state,
          zip_code: data.zip_code,
          country: data.country,
        }
      : null;

    try {
      await onSave(facilityDetails, addressDetails);

      const deviceIds = getNonEmptyArray(data.deviceIdentifiers);

      if (deviceIds.length) {
        await handleDeviceAssociation(facility?.id, deviceIds);
      }
      onClose();
    } catch (err) {
      serviceLogger.error('Failed to save facility:', err);
      showToast({
        variant: 'error',
        title: 'Save failed',
        copy: 'We could not save your changes. Please try again.',
      });
    }
  };

  /* ───────── toggle ───────── */
  const toggleDeviceInput = () => setShowDeviceInput((prev) => !prev);

  /* ───────── render ───────── */
  return (
    <BaseModalV2
      title={action === ACTION_TYPES.ADD ? 'Add Facility' : 'Edit Facility'}
      onClose={onClose}
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        {/* Facility Name */}
        <div className="form-group">
          <Label variant="gray">Facility Name</Label>
          <Input
            variant="primary"
            customClass="!max-w-full"
            placeholder="Facility Name"
            autoFocus
            {...register('name', { required: EMPTY_FIELD_ERROR })}
          />
          <ErrorMessage message={errors.name?.message} className="!static mt-2" />
        </div>

        {/* Type */}
        <div className="form-group">
          <Label variant="gray">Type</Label>
          <Input
            variant="primary"
            customClass="!max-w-full"
            placeholder="Type"
            {...register('type', { required: EMPTY_FIELD_ERROR })}
          />
          <ErrorMessage message={errors.type?.message} className="!static mt-2" />
        </div>

        {/* Address Section */}
        <h3 className="modal-subtitle">Address</h3>
        <div className="form-group">
          <Label variant="gray">Street</Label>
          <Input
            variant="primary"
            customClass="!max-w-full"
            placeholder="Street"
            {...register('street')}
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <Label variant="gray">City</Label>
            <Input
              variant="primary"
              customClass="!max-w-full"
              placeholder="City"
              {...register('city')}
            />
          </div>
          <div className="form-group">
            <Label variant="gray">State</Label>
            <Input
              variant="primary"
              customClass="!max-w-full"
              placeholder="State"
              {...register('state')}
            />
          </div>
          <div className="form-group">
            <Label variant="gray">Zip Code</Label>
            <Input
              variant="primary"
              customClass="!max-w-full"
              placeholder="Zip Code"
              {...register('zip_code')}
            />
          </div>
        </div>

        {/* Country */}
        <div className="form-group">
          <Label variant="gray">Country</Label>
          <SelectField
            label="Country"
            name="country"
            options={countryOptions}
            register={register}
            errors={errors}
          />
        </div>

        {/* Device Units */}
        {(showDeviceToggle || showDeviceInput) && (
          <div className="form-group mt-4">
            {showDeviceToggle && (
              <div className="toggle-container">
                <Label variant="gray">Associate Device Units to Facility (optional)</Label>
                <ToggleButton status={deviceInputStatus} onToggle={toggleDeviceInput} />
              </div>
            )}

            {showDeviceInput && <DeviceUnitsField control={control} errors={errors} />}
          </div>
        )}

        {/* Buttons */}
        <div className="modal-buttons">
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={() => {}} type="submit" disabled={!isValid}>
            Save
          </PrimaryButton>
        </div>
      </form>
    </BaseModalV2>
  );
};

export default FacilityModal;
